// Error boundary with rollback functionality for the wizard

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { wizardLogger } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class WizardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
    console.log('🛡️ [WizardErrorBoundary] Initialized');
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('💥 [WizardErrorBoundary] Caught error:', error);
    console.error('💥 [WizardErrorBoundary] Error info:', errorInfo);
    
    wizardLogger.error('errorBoundary', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // In production, we could trigger a rollback here
    if (process.env.NODE_ENV === 'production') {
      // Rollback to original UI by disabling the feature flag
      // This would require a page reload or state reset
      console.error('Wizard UI error - consider rolling back feature flag');
    }
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-center text-gray-900">
              Something went wrong
            </h3>
            <p className="mt-2 text-sm text-center text-gray-600">
              We encountered an error while loading the book creation wizard.
              Please refresh the page to try again.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Refresh Page
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-xs text-gray-500">
                <summary className="cursor-pointer">Error Details</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto">
                  {this.state.error.toString()}
                  {'\n\nStack trace:\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            {/* Always show debug info if ?debug=true is in URL */}
            {typeof window !== 'undefined' && window.location.search.includes('debug=true') && this.state.error && (
              <div className="mt-4 p-4 bg-yellow-100 border-2 border-yellow-400 rounded">
                <h4 className="font-bold text-yellow-800">DEBUG INFO:</h4>
                <pre className="mt-2 text-xs overflow-auto">
{JSON.stringify({
  errorMessage: this.state.error.message,
  errorName: this.state.error.name,
  errorStack: this.state.error.stack,
  timestamp: new Date().toISOString(),
  location: window.location.href,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'
}, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
